/** 选区辅助：mask 合并、蚂蚁线、flood fill */

export const SELECTION_OVERLAY_ID = 'designer-region-selection';
export const SELECTION_TRANSIENT_FLAG = 'designerSelectionTransient';

/** 选区 overlay / 绘制过程中的临时图形，不应出现在图层面板 */
export function isSelectionOverlayObject(obj) {
  if (!obj) return false;
  if (obj.id === SELECTION_OVERLAY_ID) return true;
  if (typeof obj.id === 'string' && obj.id.startsWith('designer-region-selection')) return true;
  if (obj[SELECTION_TRANSIENT_FLAG] || obj.designerSelectionTransient) return true;
  if (obj.excludeFromExport && (obj.id?.startsWith?.('designer-') || obj.designerSelectionTransient)) {
    return true;
  }
  return false;
}

export function markSelectionTransient(obj) {
  if (!obj) return obj;
  obj[SELECTION_TRANSIENT_FLAG] = true;
  // fabric 自定义属性同步一份，避免 Group 事件里读不到 symbol/非枚举字段
  obj.designerSelectionTransient = true;
  if (typeof obj.set === 'function') {
    obj.set('designerSelectionTransient', true);
    obj.set('excludeFromExport', true);
  }
  obj.excludeFromExport = true;
  obj.selectable = false;
  obj.evented = false;
  return obj;
}

export const DEFAULT_SELECTION_SETTINGS = {
  feather: 0,
  antiAlias: true,
  mode: 'new',
};

export const DEFAULT_MAGIC_WAND_SETTINGS = {
  tolerance: 32,
  sampleSize: 'point',
  antiAlias: true,
  mode: 'new',
};

export function clampTolerance(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return 32;
  return Math.min(255, Math.max(0, Math.round(n)));
}

export function clampFeather(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.min(250, Math.max(0, Math.round(n)));
}

/** 是否有有效像素选区 */
export function hasActiveSelectionMask(mask) {
  return Boolean(mask?.some?.((v) => v));
}

/**
 * 解析实际合并模式。
 * 无原选区时：add/subtract/intersect 一律按新建（默认增加），与 PS 一致。
 */
export function resolveSelectionMergeMode(existing, mode) {
  if (!hasActiveSelectionMask(existing)) return 'new';
  if (mode === 'add' || mode === 'subtract' || mode === 'intersect' || mode === 'new') {
    return mode;
  }
  return 'new';
}

/** 合并选区 mask：new / add / subtract / intersect */
export function mergeSelectionMasks(existing, incoming, mode, size) {
  const out = new Uint8Array(size);
  const resolved = resolveSelectionMergeMode(existing, mode);
  if (!existing || resolved === 'new') {
    out.set(incoming);
    return out;
  }
  for (let i = 0; i < size; i += 1) {
    const a = existing[i] ? 1 : 0;
    const b = incoming[i] ? 1 : 0;
    if (resolved === 'add') out[i] = a || b ? 1 : 0;
    else if (resolved === 'subtract') out[i] = a && !b ? 1 : 0;
    else if (resolved === 'intersect') out[i] = a && b ? 1 : 0;
    else out[i] = b;
  }
  return out;
}

/** 3×3 或单点取样颜色 */
export function samplePixelColor(imageData, x, y, sampleSize = 'point') {
  const { width, height, data } = imageData;
  const sx = Math.floor(x);
  const sy = Math.floor(y);
  if (sx < 0 || sy < 0 || sx >= width || sy >= height) return null;

  if (sampleSize !== '3x3') {
    const idx = (sy * width + sx) * 4;
    return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
  }

  let r = 0;
  let g = 0;
  let b = 0;
  let a = 0;
  let count = 0;
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      const px = sx + dx;
      const py = sy + dy;
      if (px < 0 || py < 0 || px >= width || py >= height) continue;
      const idx = (py * width + px) * 4;
      r += data[idx];
      g += data[idx + 1];
      b += data[idx + 2];
      a += data[idx + 3];
      count += 1;
    }
  }
  if (!count) return null;
  return [
    Math.round(r / count),
    Math.round(g / count),
    Math.round(b / count),
    Math.round(a / count),
  ];
}

function colorDistance(data, idx, sample, tolerance) {
  const dr = Math.abs(data[idx] - sample[0]);
  const dg = Math.abs(data[idx + 1] - sample[1]);
  const db = Math.abs(data[idx + 2] - sample[2]);
  const da = Math.abs(data[idx + 3] - sample[3]);
  return dr + dg + db + da <= tolerance * 4;
}

/** flood fill，支持 3×3 取样起点 */
export function floodFillMask(imageData, startX, startY, tolerance, sampleSize = 'point') {
  const { width, height, data } = imageData;
  const sx = Math.floor(startX);
  const sy = Math.floor(startY);
  if (sx < 0 || sy < 0 || sx >= width || sy >= height) return null;

  const sample = samplePixelColor(imageData, sx, sy, sampleSize);
  if (!sample) return null;

  const visited = new Uint8Array(width * height);
  const mask = new Uint8Array(width * height);
  const stack = [[sx, sy]];
  const tol = clampTolerance(tolerance);

  while (stack.length) {
    const [x, y] = stack.pop();
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    const pi = y * width + x;
    if (visited[pi]) continue;
    visited[pi] = 1;

    const i = pi * 4;
    if (!colorDistance(data, i, sample, tol)) continue;

    mask[pi] = 1;
    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }

  return { mask, width, height };
}

/** 简单盒式羽化 */
export function featherMask(mask, width, height, radius) {
  const r = clampFeather(radius);
  if (r <= 0) return mask;
  const out = new Uint8Array(mask.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pi = y * width + x;
      if (!mask[pi]) continue;
      for (let dy = -r; dy <= r; dy += 1) {
        for (let dx = -r; dx <= r; dx += 1) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          out[ny * width + nx] = 1;
        }
      }
    }
  }
  return out;
}

/** 将局部 mask 嵌入画布坐标 */
export function embedMaskInCanvas(localMask, localW, localH, offsetX, offsetY, canvasW, canvasH) {
  const out = new Uint8Array(canvasW * canvasH);
  for (let y = 0; y < localH; y += 1) {
    for (let x = 0; x < localW; x += 1) {
      if (!localMask[y * localW + x]) continue;
      const cx = x + Math.round(offsetX);
      const cy = y + Math.round(offsetY);
      if (cx < 0 || cy < 0 || cx >= canvasW || cy >= canvasH) continue;
      out[cy * canvasW + cx] = 1;
    }
  }
  return out;
}

export function rasterizeRectMask(left, top, width, height, canvasW, canvasH) {
  const mask = new Uint8Array(canvasW * canvasH);
  const x0 = Math.max(0, Math.floor(left));
  const y0 = Math.max(0, Math.floor(top));
  const x1 = Math.min(canvasW, Math.ceil(left + width));
  const y1 = Math.min(canvasH, Math.ceil(top + height));
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      mask[y * canvasW + x] = 1;
    }
  }
  return mask;
}

export function rasterizeEllipseMask(left, top, width, height, canvasW, canvasH) {
  const mask = new Uint8Array(canvasW * canvasH);
  const cx = left + width / 2;
  const cy = top + height / 2;
  const rx = Math.abs(width / 2);
  const ry = Math.abs(height / 2);
  const x0 = Math.max(0, Math.floor(left));
  const y0 = Math.max(0, Math.floor(top));
  const x1 = Math.min(canvasW, Math.ceil(left + width));
  const y1 = Math.min(canvasH, Math.ceil(top + height));
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const dx = (x - cx) / (rx || 1);
      const dy = (y - cy) / (ry || 1);
      if (dx * dx + dy * dy <= 1) {
        mask[y * canvasW + x] = 1;
      }
    }
  }
  return mask;
}

export function rasterizePolygonMask(points, canvasW, canvasH) {
  const cvs = document.createElement('canvas');
  cvs.width = canvasW;
  cvs.height = canvasH;
  const ctx = cvs.getContext('2d');
  if (!ctx || !points?.length) return new Uint8Array(canvasW * canvasH);

  ctx.fillStyle = '#fff';
  ctx.beginPath();
  points.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.closePath();
  ctx.fill();

  const img = ctx.getImageData(0, 0, canvasW, canvasH);
  const mask = new Uint8Array(canvasW * canvasH);
  for (let i = 0; i < mask.length; i += 1) {
    mask[i] = img.data[i * 4 + 3] > 0 ? 1 : 0;
  }
  return mask;
}

/** 将 fabric 形状栅格化为 mask（画布坐标） */
export function rasterizeShapeToMask(shape, canvasWidth, canvasHeight) {
  const cvs = document.createElement('canvas');
  cvs.width = canvasWidth;
  cvs.height = canvasHeight;
  const ctx = cvs.getContext('2d');
  if (!ctx) return new Uint8Array(canvasWidth * canvasHeight);

  ctx.fillStyle = '#fff';
  if (shape.type === 'rect') {
    ctx.fillRect(shape.left, shape.top, shape.width, shape.height);
  } else if (shape.type === 'polygon' && shape.points) {
    ctx.beginPath();
    shape.points.forEach((p, i) => {
      const x = shape.left + p.x;
      const y = shape.top + p.y;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fill();
  } else if (shape.points) {
    ctx.beginPath();
    const pts = shape.points;
    for (let i = 0; i < pts.length; i += 2) {
      if (i === 0) ctx.moveTo(pts[i], pts[i + 1]);
      else ctx.lineTo(pts[i], pts[i + 1]);
    }
    ctx.closePath();
    ctx.fill();
  }

  const img = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
  const mask = new Uint8Array(canvasWidth * canvasHeight);
  for (let i = 0; i < mask.length; i += 1) {
    mask[i] = img.data[i * 4 + 3] > 0 ? 1 : 0;
  }
  return mask;
}

/**
 * Moore 邻域轮廓追踪 — 只沿「前景且有背景 4 邻」的边缘像素行走，
 * 得到有序边界点，供魔棒/不规则选区蚂蚁线使用。
 * @returns {{x:number,y:number}[]|null}
 */
export function maskToContourPoints(mask, width, height, offsetX = 0, offsetY = 0, step = 2) {
  if (!mask?.length || width < 1 || height < 1) return null;

  const at = (x, y) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return 0;
    return mask[y * width + x] ? 1 : 0;
  };
  const isEdge = (x, y) => (
    at(x, y)
    && (!at(x - 1, y) || !at(x + 1, y) || !at(x, y - 1) || !at(x, y + 1))
  );

  let startX = -1;
  let startY = -1;
  outer: for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!isEdge(x, y)) continue;
      startX = x;
      startY = y;
      break outer;
    }
  }
  if (startX < 0) return null;

  // E, NE, N, NW, W, SW, S, SE
  const DX = [1, 1, 0, -1, -1, -1, 0, 1];
  const DY = [0, -1, -1, -1, 0, 1, 1, 1];

  const points = [];
  let x = startX;
  let y = startY;
  let backDir = 4;
  const maxSteps = width * height * 2;
  let steps = 0;

  do {
    points.push({ x: x + offsetX, y: y + offsetY });
    let found = false;
    const startSearch = (backDir + 6) % 8;
    for (let i = 0; i < 8; i += 1) {
      const nd = (startSearch + i) % 8;
      const nx = x + DX[nd];
      const ny = y + DY[nd];
      if (!isEdge(nx, ny)) continue;
      backDir = (nd + 4) % 8;
      x = nx;
      y = ny;
      found = true;
      break;
    }
    if (!found) break;
    steps += 1;
  } while ((x !== startX || y !== startY) && steps < maxSteps);

  if (points.length < 3) return null;

  const stride = Math.max(1, Math.round(step));
  if (stride === 1) return points;

  const simplified = [];
  for (let i = 0; i < points.length; i += stride) {
    simplified.push(points[i]);
  }
  if (simplified.length >= 3) {
    const first = simplified[0];
    const last = simplified[simplified.length - 1];
    if (Math.hypot(first.x - last.x, first.y - last.y) > stride) {
      simplified.push({ x: first.x, y: first.y });
    }
    return simplified;
  }
  return points;
}

/** @deprecated 使用 maskToContourPoints（有序轮廓） */
export function maskToPolygonPoints(mask, width, height, offsetX, offsetY, step = 4) {
  return maskToContourPoints(mask, width, height, offsetX, offsetY, step);
}

/**
 * 提取 mask 全部外/内轮廓（多孔洞、多连通区域），供加减选区蚂蚁线使用。
 * @returns {{x:number,y:number}[][]}
 */
export function maskToAllContours(mask, width, height, offsetX = 0, offsetY = 0, step = 2) {
  if (!mask?.length || width < 1 || height < 1) return [];

  const edgeVisited = new Uint8Array(width * height);
  const at = (x, y) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return 0;
    return mask[y * width + x] ? 1 : 0;
  };
  const isEdge = (x, y) => (
    at(x, y)
    && (!at(x - 1, y) || !at(x + 1, y) || !at(x, y - 1) || !at(x, y + 1))
  );

  const DX = [1, 1, 0, -1, -1, -1, 0, 1];
  const DY = [0, -1, -1, -1, 0, 1, 1, 1];
  const contours = [];
  const stride = Math.max(1, Math.round(step));

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pi = y * width + x;
      if (edgeVisited[pi] || !isEdge(x, y)) continue;

      const points = [];
      let cx = x;
      let cy = y;
      let backDir = 4;
      const maxSteps = width * height * 2;
      let steps = 0;

      do {
        const cpi = cy * width + cx;
        edgeVisited[cpi] = 1;
        points.push({ x: cx + offsetX, y: cy + offsetY });
        let found = false;
        const startSearch = (backDir + 6) % 8;
        for (let i = 0; i < 8; i += 1) {
          const nd = (startSearch + i) % 8;
          const nx = cx + DX[nd];
          const ny = cy + DY[nd];
          if (!isEdge(nx, ny)) continue;
          // 允许走未访问边，也允许回到起点闭合
          const npi = ny * width + nx;
          if (edgeVisited[npi] && !(nx === x && ny === y)) continue;
          backDir = (nd + 4) % 8;
          cx = nx;
          cy = ny;
          found = true;
          break;
        }
        if (!found) break;
        steps += 1;
      } while ((cx !== x || cy !== y) && steps < maxSteps);

      if (points.length < 3) continue;

      const simplified = [];
      for (let i = 0; i < points.length; i += stride) {
        simplified.push(points[i]);
      }
      if (simplified.length >= 3) {
        const first = simplified[0];
        const last = simplified[simplified.length - 1];
        if (Math.hypot(first.x - last.x, first.y - last.y) > stride) {
          simplified.push({ x: first.x, y: first.y });
        }
        contours.push(simplified);
      } else {
        contours.push(points);
      }
    }
  }

  return contours;
}

export function countMaskPixels(mask) {
  let n = 0;
  for (let i = 0; i < mask.length; i += 1) {
    if (mask[i]) n += 1;
  }
  return n;
}

/** 选区 mask 的外接矩形 */
export function getMaskBounds(mask, width, height) {
  if (!mask?.length) return null;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[y * width + x]) continue;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < minX || maxY < minY) return null;
  return {
    left: minX,
    top: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

/** 选区 mask → 紧凑 RLE（历史快照用） */
export function encodeSelectionMaskRle(mask) {
  if (!mask?.length) return null;
  const runs = [];
  let i = 0;
  while (i < mask.length) {
    const value = mask[i] ? 1 : 0;
    let j = i + 1;
    while (j < mask.length && (mask[j] ? 1 : 0) === value) j += 1;
    runs.push(value, j - i);
    i = j;
  }
  return runs;
}

/** RLE → 选区 mask */
export function decodeSelectionMaskRle(runs, size) {
  if (!runs?.length || !size) return null;
  const out = new Uint8Array(size);
  let cursor = 0;
  for (let i = 0; i + 1 < runs.length && cursor < size; i += 2) {
    const value = runs[i] ? 1 : 0;
    const count = Math.max(0, Number(runs[i + 1]) || 0);
    for (let k = 0; k < count && cursor < size; k += 1) {
      out[cursor] = value;
      cursor += 1;
    }
  }
  return out;
}

/** 选区指纹（历史去重） */
export function selectionHistoryFingerprint(snapshot) {
  if (!snapshot?.maskRle?.length) return 'none';
  const runs = snapshot.maskRle;
  let filled = 0;
  for (let i = 0; i + 1 < runs.length; i += 2) {
    if (runs[i]) filled += Number(runs[i + 1]) || 0;
  }
  return `${snapshot.w || 0}x${snapshot.h || 0}:${filled}:${runs.length}:${runs[0]}:${runs[1] || 0}:${runs[runs.length - 2]}:${runs[runs.length - 1]}`;
}

/** 平移选区 mask */
export function shiftSelectionMask(mask, width, height, dx, dy) {
  const out = new Uint8Array(mask.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[y * width + x]) continue;
      const nx = x + dx;
      const ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
      out[ny * width + nx] = 1;
    }
  }
  return out;
}

/** 缩放选区 mask（相对原外接矩形） */
export function scaleSelectionMask(mask, width, height, bounds, scaleX, scaleY) {
  const out = new Uint8Array(mask.length);
  const cx = bounds.left + bounds.width / 2;
  const cy = bounds.top + bounds.height / 2;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[y * width + x]) continue;
      const sx = Math.round(cx + (x - cx) * scaleX);
      const sy = Math.round(cy + (y - cy) * scaleY);
      if (sx < 0 || sy < 0 || sx >= width || sy >= height) continue;
      out[sy * width + sx] = 1;
    }
  }
  return out;
}
